import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Paperclip, Trash2, X } from 'lucide-react';
import { KbAttachmentRow } from '@/components/knowledge/KbAttachments';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { BlogBodyEditor } from '@/pages/marketing/BlogBodyEditor';
import { toast } from '@/store/toastStore';
import {
  createKnowledgeBaseDocument,
  deleteKbAttachment,
  getKnowledgeBaseDocument,
  kbQueryKeys,
  updateKnowledgeBaseDocument,
  uploadKbAttachment,
  type KbDocumentAttachment,
} from '@/services/knowledge-base.service';

interface FormState {
  title: string;
  body: string;
  category: string;
  url: string;
  tags: string[];
  status: 'active' | 'archived' | 'draft';
}

const EMPTY: FormState = {
  title: '',
  body: '',
  category: '',
  url: '',
  tags: [],
  status: 'active',
};

const ATTACHMENT_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.png,.jpg,.jpeg,.gif,.webp,.svg';

export function DocumentEditor() {
  const params = useParams<{ id?: string }>();
  const documentId = params.id && params.id !== 'new' ? Number(params.id) : 0;
  const isEdit = documentId > 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [tagInput, setTagInput] = useState('');
  const [attachments, setAttachments] = useState<KbDocumentAttachment[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const attachInputRef = useRef<HTMLInputElement | null>(null);

  const docQuery = useQuery({
    queryKey: kbQueryKeys.document(documentId),
    queryFn: () => getKnowledgeBaseDocument(documentId),
    enabled: isEdit,
  });

  useEffect(() => {
    if (docQuery.data) {
      setForm({
        title: docQuery.data.title,
        body: docQuery.data.body ?? '',
        category: docQuery.data.category ?? '',
        url: docQuery.data.url ?? '',
        tags: docQuery.data.tags ?? [],
        status: ((docQuery.data.status as FormState['status']) ?? 'active'),
      });
      setAttachments(docQuery.data.attachments ?? []);
    }
  }, [docQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title.trim(),
        body: form.body,
        bodyFormat: 'html' as const,
        category: form.category.trim(),
        url: form.url.trim(),
        tags: form.tags,
        status: form.status,
        attachmentIds: attachments
          .filter((a) => a.kbDocumentId === null)
          .map((a) => a.kbDocumentAttachmentId),
      };
      return isEdit
        ? updateKnowledgeBaseDocument(documentId, payload)
        : createKnowledgeBaseDocument(payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.all });
      toast.success(isEdit ? 'Document updated' : 'Document created');
      navigate(`../${saved.kbDocumentId}`);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to save document';
      toast.error(msg);
    },
  });

  // Inline image upload — invoked by BlogBodyEditor's "Upload Image" toolbar
  // button. We pass `kbDocumentId` only when editing; on create the row is
  // orphaned until save links it via attachmentIds.
  async function uploadInlineImage(file: File): Promise<string> {
    try {
      const result = await uploadKbAttachment(file, {
        kbDocumentId: isEdit ? documentId : undefined,
        isInline: true,
      });
      // Track even inline images locally so create-flow saves can link them
      // (prevents the next /uploads cleanup job from deleting them as orphans).
      setAttachments((curr) => [...curr, result]);
      return result.url;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Image upload failed';
      toast.error(msg);
      throw err;
    }
  }

  async function handleAttachmentsPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    setIsUploadingAttachment(true);
    try {
      for (const file of files) {
        try {
          const result = await uploadKbAttachment(file, {
            kbDocumentId: isEdit ? documentId : undefined,
            isInline: false,
          });
          setAttachments((curr) => [...curr, result]);
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Upload failed';
          toast.error(`${file.name}: ${msg}`);
        }
      }
    } finally {
      setIsUploadingAttachment(false);
    }
  }

  async function removeAttachment(att: KbDocumentAttachment) {
    try {
      await deleteKbAttachment(att.kbDocumentAttachmentId);
      setAttachments((curr) =>
        curr.filter((a) => a.kbDocumentAttachmentId !== att.kbDocumentAttachmentId),
      );
    } catch {
      toast.error(`Could not remove ${att.originalFilename}`);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    saveMutation.mutate();
  }

  function addTag() {
    const value = tagInput.trim();
    if (!value) return;
    if (form.tags.includes(value)) {
      setTagInput('');
      return;
    }
    setForm((f) => ({ ...f, tags: [...f.tags, value] }));
    setTagInput('');
  }

  function handleTagKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  }

  if (isEdit && docQuery.isLoading) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading...</div>;
  }

  if (isEdit && docQuery.data && !docQuery.data.editable) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        This document is synced from an external source and cannot be edited. Pause the source on
        the Connections tab to take over editing manually.
      </div>
    );
  }

  // Only non-inline attachments show in the user-visible list.
  const visibleAttachments = attachments.filter((a) => !a.isInline);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <Link to=".." className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="h-4 w-4" />
          Back to library
        </Link>
        <div className="flex gap-2">
          <Link to="..">
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          <Button type="submit" loading={saveMutation.isPending}>
            {isEdit ? 'Save Changes' : 'Create Document'}
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
        <Input
          label="Title"
          required
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        />
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Category"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            placeholder="e.g. Memberships"
          />
          <Input
            label="External URL"
            value={form.url}
            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            placeholder="https://..."
          />
        </div>

        <FormField label="Tags">
          <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1.5">
            {form.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
              >
                {t}
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-700"
                  onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }))}
                  aria-label={`Remove tag ${t}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              type="text"
              className="flex-1 min-w-[120px] border-0 bg-transparent px-1 py-0.5 text-sm focus:outline-none focus:ring-0"
              placeholder="Add tag and press Enter"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={addTag}
            />
          </div>
        </FormField>

        <FormField label="Status">
          <select
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as FormState['status'] }))}
            className="form-input-base"
          >
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </FormField>

        <FormField label="Body">
          <BlogBodyEditor
            html={form.body}
            onChange={(html) => setForm((f) => ({ ...f, body: html }))}
            onUploadImage={uploadInlineImage}
          />
        </FormField>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900">Attachments</h3>
            <span className="text-xs text-slate-500">
              {visibleAttachments.length} file{visibleAttachments.length === 1 ? '' : 's'}
            </span>
          </div>
          <input
            ref={attachInputRef}
            type="file"
            accept={ATTACHMENT_ACCEPT}
            multiple
            className="hidden"
            onChange={handleAttachmentsPicked}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={isUploadingAttachment}
            leftIcon={
              isUploadingAttachment ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Paperclip className="h-3.5 w-3.5" />
              )
            }
            onClick={() => attachInputRef.current?.click()}
          >
            {isUploadingAttachment ? 'Uploading…' : 'Add Files'}
          </Button>
        </div>

        {visibleAttachments.length === 0 ? (
          <p className="text-sm text-slate-500">
            No attachments yet. PDFs, Office docs, images and ZIP archives up to 50&nbsp;MB are
            supported.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visibleAttachments.map((att) => (
              <KbAttachmentRow
                key={att.kbDocumentAttachmentId}
                attachment={att}
                trailing={
                  <button
                    type="button"
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    aria-label={`Remove ${att.originalFilename}`}
                    onClick={() => removeAttachment(att)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                }
              />
            ))}
          </ul>
        )}
      </div>
    </form>
  );
}
